// @ts-check
import Vue from 'vue';
import LocalStorageService from '@bto-services/storage/local-storage-service';
import { BETSLIP_LAYOUTS, BETSLIP_STORAGE_KEY, BETSLIP_TYPES, getLayout, BETSLIP_ACCEPT_ODD_TYPE } from 'bto-models/betslip';
import { getTabs } from './internals/tabs';


/**
 * @typedef {import('bto-models/betslip/interfaces').BetslipItem} BetslipItem
 * @typedef {import('bto-models/betslip/interfaces').BetslipMatchItem} BetslipMatchItem
 */

/**
 * Get default selected tab from global settings
 */
function getDefaultTab() {
	if (getLayout() !== BETSLIP_LAYOUTS.TABS) {
		return;
	}
	const tabs = getTabs();
	if (tabs.length === 0) {
		return;
	}
	const combiTab = tabs.find(t => t.id === BETSLIP_TYPES.COMBI);
	if (combiTab) {
		return combiTab.id;
	}
	return tabs[0].id;
}
/**
 * Get default accept odd change
 */
function getDefaultAcceptOdd() {
	const defaultAOC = APP_SETTINGS.betslip_v2.defaultAcceptOddChanges_v2.trim().toUpperCase();
	return BETSLIP_ACCEPT_ODD_TYPE[defaultAOC];
}

/**
 * Get default state for betslip
 * @type {()=>import('bto-models/betslip/interfaces').BetslipStore}
 */
export const getDefaultState = () => ({
	/**
	 * Flag for accept odd change
	 * @type {import('bto-models/betslip/interfaces').BETSLIP_ACCEPT_ODD_TYPE_ENUMS}
	 */
	acceptOddChange: getDefaultAcceptOdd(),
	/**
	 * Flag for forced manual authorization
	 */
	manualAuth: false,
	/**
	 * Note sent during placement on agent
	 * @type {string}
	 */
	note: '',
	/**
	 * @type {boolean}
	 */
	showBankers: APP_SETTINGS.betslip_v2.useBankers && APP_SETTINGS.betslip_v2.showBankers,
	/**
	 * Map of bet items
	 * @type {{[matchId_marketId_outcomeId:string]:BetslipItem}} 
	 */
	items: {},
	/**
	 * Map of uniqure bet match items
	 * @type {{[matchId:string]:BetslipMatchItem}}
	 */
	matchItems: {},
	/**
	 * Map of fold -> stake(number in string format) for fold
	 * @type {{[fold:number]:string}}
	 */
	folds: {},
	/**
	 * Map of fold -> number of lines for fold
	 * @type {{[fold:number]:number}}
	 */
	foldLines: {},
	/**
	 * Map of fold -> total odds for fold if single line fold
	 * @type {{[fold:number]:string}}
	 */
	foldOdds: {},
	/**
	 * Map of single item -> stake (number in string format) for single item
	 * @type {{[matchId_marketId_outcomeId:string]:string}}
	 */
	singles: {},
	/**
	 * Map of single item -> max winnings for single
	 * @type {{[matchId_marketId_outcomeId:string]:number}}
	 */
	singleBonuses: {},
	/**
	 * Map of single item -> flag if raceOdd is selected for item
	 * @type {{[matchId_marketId_outcomeId:string]:boolean}}
	 */
	raceOdds: {},
	/**
	 * Map of single item -> flag if banker is selected for item
	 * @type {{[matchId_marketId_outcomeId:string]:boolean}}
	 */
	bankers: {},
	bonuses: {},
	totals: {},
	freebetId: 0,
	netFreebet: false,
	selectedTab: getDefaultTab(),
	calculationInProgress: false,
	quickBetActive: false,
	quickBetStake: '',
	bookedBetslipDetails: {
		bookCode: null,
		matchItems: [],
	},
	algoItems: {},
});

/**
 * Get default/saved state from local storage
 * @param {ReturnType<typeof getDefaultState>} savedState - last saved state if any
 * @returns default state plus saved state
 */
const getSavedState = (savedState) => ({
	...getDefaultState(),
	...savedState,
})

const savedBetslipState = LocalStorageService.get(BETSLIP_STORAGE_KEY) || {};

export const state = Vue.observable(getSavedState(savedBetslipState));
